<template>
  <div>
    <div :class="$style.iOSEnrollmentContainer">
      <p :class="$style.iOSEnrollmentDetails">
        You can control if a user's personal Apple iOS Device can enroll in JumpCloud's
        MDM to access company resources. Select Allow users to enroll mobile devices,
        and instruct the user to log into the User Portal and go to Security > Enroll
        Your iOS device.  After the user scans a QR code to enroll the phone in MDM, the
        enrolled iOS device is visible in the Admin Portal.
      </p>
      <CheckboxWithLabel
        label="Allow users to enroll personal mobile devices and access
        <b>Enroll Your iOS Device</b> in the User Portal."
        :preventDefault="true"
        :value="iOSEnrollmentValue"
        @change="value => onClick(value)"
      />
      <div
        v-if="iOSEnrollmentValue"
        :class="$style.defaultGroupContainer"
      >
        <p :class="$style.iOSEnrollmentDetails">
          Select the Device Group to automatically add enrolled devices to:
        </p>
        <div>
          <DropDownMultiSelectSearch
            v-model="deviceGroup"
            :alwaysShowSearch="true"
            :class="$style.MdmDefaultGroup"
            :displayValue="true"
            :multiselect="false"
            :name="name"
            :options="deviceGroupOptions"
            :showFullValueText="true"
            @input="v => handleGroupChange(v)"
          >
            <template v-slot:footer>
              <div>
                <Button
                  :hasNoBackground="true"
                  text="Create New Group >"
                  type="informational"
                  @click="navigateToNewGroup"
                />
              </div>
            </template>
          </DropDownMultiSelectSearch>
        </div>
      </div>
    </div>
    <AlertContainer>
      <Alert
        v-if="successText"
        :delay="7000"
        type="success"
        @hidden="handleHidden"
      >
        <span>{{ successText }}</span>
      </Alert>
      <Alert
        v-if="errorText"
        :delay="7000"
        type="error"
        @hidden="handleHidden"
      >
        <span>{{ errorText }}</span>
      </Alert>
    </AlertContainer>
  </div>
</template>

<script>
import {
  Button,
} from '@jumpcloud/ui-components';
import {
  cloneDeep, findIndex, get, isEmpty,
} from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import Alert from 'client/components/Alert/Alert.vue';
import AlertContainer from 'client/components/Alert/AlertContainer';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import Constants, {
  ItemTypeConsts, ItemTypeDisplayNames, ItemTypeRoutes,
  StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import DropDownMultiSelectSearch from 'client/components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import Util from 'client/util/Util';

const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const GroupCollection = StoreModuleMap[ItemTypeConsts.group];
const { error, success } = Constants;

export default {
  name: 'MdmIOSUserEnrollment',

  components: {
    Alert,
    AlertContainer,
    Button,
    CheckboxWithLabel,
    DropDownMultiSelectSearch,
  },

  mixins: [MdmSettingsMixin],

  notify,

  data: () => ({
    deviceGroup: [],
    errorText: '',
    iOSEnrollmentValue: false,
    name: ItemTypeDisplayNames.deviceGroup,
    successText: '',
  }),

  computed: {
    ...mapState(mdmSettingsModel, ['item']),

    ...mapGetters(GroupCollection, {
      items: 'getCollection',
    }),

    deviceGroupOptions() {
      const noneOption = { name: 'None', value: '' };
      if (this.items) {
        const groupOptions = this.items.map(group => ({ name: group.name, value: group.id }));
        const options = [noneOption, ...groupOptions];
        return options;
      }
      return [noneOption];
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(mdmSettingsModel, [
      'fetchMdmSettings',
      'updateDep',
      'updateItem',
    ]),

    ...mapActions(GroupCollection, [
      'fetchEntireCollection',
      'setFilter',
    ]),

    async handleGroupChange(val) {
      const deviceGroup = val[0];
      const updatedDevices = cloneDeep(this.item.mdmDevices);
      if (updatedDevices[0]) {
        updatedDevices[0].defaultIosUserEnrollmentDeviceGroupID = deviceGroup;
        try {
          await this.updateDep({
            allowMobileUserEnrollment: this.iOSEnrollmentValue,
            defaultIosUserEnrollmentDeviceGroupID: deviceGroup,
          });
          this.deviceGroup = [deviceGroup];
          this.$options.notify(success, 'Successfully set default device group');
        } catch {
          this.$options.notify(error, 'Unable to set default device group');
        }
      }
    },

    handleHidden() {
      this.successText = '';
      this.errorText = '';
    },

    async handleMounted() {
      this.iOSEnrollmentValue = get(this.item.mdmDevices[0], 'allowMobileUserEnrollment', false);
      try {
        const filter = {
          name: 'type',
          operator: 'eq',
          value: ItemTypeConsts.systemGroup,
        };
        this.setFilter({ filter });

        await this.fetchEntireCollection();
        await this.setValueOnLoad();
        this.showLoadingOverlay = false;
      } catch {
        this.showLoadingOverlay = false;
        const message = `Unable to get the ${this.displayName} settings.`;
        this.$options.notify(error, message);
      }
    },

    handleRedirect() {
      ModalEventBus.close();
      Util.redirect(`${ItemTypeRoutes[ItemTypeConsts.deviceGroup]}/new`);
    },

    navigateToNewGroup() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          headerText: 'You are about to leave this page',
          bodyText: 'You will be redirected to the Groups page to set up a new group. Once finished, navigate back to this page to designate it as your default group for new Automated Device Enrollment devices.',
          handleCancel: () => ModalEventBus.close(),
          handleContinue: () => this.handleRedirect(),
          showClose: true,
        },
      });
    },

    async onClick(value) {
      this.iOSEnrollmentValue = value;
      try {
        await this.updateDep({ allowMobileUserEnrollment: value });
        this.successText = `User iOS enrollment successfully ${value ? 'enabled' : 'disabled'}`;
      } catch {
        this.iOSEnrollmentValue = !value;
        this.errorText = 'The service encountered a temporary error and could not update the Device Manager settings. Please try again later.';
      }
    },

    async setValueOnLoad() {
      // Wait for MDM settings so that deviceSetupSettings has values
      await this.fetchMdmSettings();
      const noneOption = this.deviceGroupOptions[0].value;

      if (isEmpty(this.item.mdmDevices)) {
        this.deviceGroup = [noneOption];
        return;
      }

      const defaultGroupId = get(this.item.mdmDevices[0], 'defaultIosUserEnrollmentDeviceGroupID', '');
      if (!defaultGroupId) {
        this.deviceGroup = [noneOption];
        return;
      }

      const index = findIndex(this.deviceGroupOptions, o => o.value === defaultGroupId);
      if (index > -1) {
        const defaultValue = this.deviceGroupOptions[index].value;
        this.deviceGroup = [defaultValue];
      }
    },
  },
};
</script>

<style module>
@import './css/mdmWelcomeExperienceStyles.css';
@import '../css/flex.css';

@value (jcGrey100) from '~css/brand-colors.css';

.defaultGroupContainer {
  padding-left: 1.6rem;
}

.iOSEnrollmentContainer {
  border-top: 1px solid jcGrey100;
}

.iOSEnrollmentDetails {
  padding-top: 1rem;
}

.MdmDefaultGroup {
  --valueTextTextOverflow: ellipsis;
  --valueTextWhiteSpace: nowrap;
  --valueTextMaxWidth: 20rem;
  --valueTextPadding: 0.5rem;
  --valueTextOverflow: hidden;
  --valueTextFlex: flex;
  --valueTextAlignment: center;
}
</style>
